import React, { useState, useEffect } from 'react';
import { ethers } from 'ethers';
import { 
    Wallet, 
    Link as LinkIcon, 
    Unlink, 
    CheckCircle, 
    AlertCircle, 
    Loader,
    Shield,
    Info,
    ExternalLink
} from 'lucide-react';
import './WalletConnect.css';

const WalletConnect = ({ userToken, userId }) => {
    const [walletStatus, setWalletStatus] = useState({
        isLinked: false,
        walletAddress: null,
        linkedAt: null,
        signatureVerified: false
    });
    const [currentAddress, setCurrentAddress] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isConnecting, setIsConnecting] = useState(false);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');

    // 🔧 Mobile detection
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    const isMetaMaskBrowser = window.ethereum?.isMetaMask;
    const hasEthereumProvider = typeof window.ethereum !== 'undefined';

    useEffect(() => {
        checkWalletStatus();
        checkMetaMaskConnection();

        if (window.ethereum) {
            window.ethereum.on('accountsChanged', handleAccountsChanged);
            window.ethereum.on('chainChanged', () => window.location.reload());
        }

        return () => {
            if (window.ethereum) {
                window.ethereum.removeListener('accountsChanged', handleAccountsChanged);
            }
        };
    }, [userToken]);

    const handleAccountsChanged = (accounts) => {
        if (accounts.length === 0) {
            setCurrentAddress(null);
        } else {
            setCurrentAddress(accounts[0]);
            checkWalletStatus();
        }
    };

    const checkMetaMaskConnection = async () => {
        if (window.ethereum) {
            try {
                const accounts = await window.ethereum.request({ 
                    method: 'eth_accounts' 
                });
                if (accounts.length > 0) {
                    setCurrentAddress(accounts[0]);
                }
            } catch (err) {
                console.error('Error checking MetaMask:', err);
            }
        }
    };

    const checkWalletStatus = async () => {
        try {
            console.log('🔍 Checking wallet status...');
            const response = await fetch(`${process.env.REACT_APP_API_URL}/blockchain/wallet/status`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${userToken}`,
                    'Content-Type': 'application/json'
                }
            });

            console.log('📡 Status response:', response.status);

            if (response.ok) {
                const data = await response.json();
                console.log('✅ Wallet status:', data);
                setWalletStatus(data);
            } else {
                console.error('❌ Failed to fetch wallet status:', response.status);
                const errorText = await response.text();
                console.error('Error details:', errorText);
            }
        } catch (err) {
            console.error('❌ Error checking wallet status:', err);
            setError('Failed to load wallet status. Please refresh the page.');
        } finally {
            setIsLoading(false);
        }
    };

    // 🔧 Open MetaMask app via deep link (for mobile)
    const openMetaMaskDeepLink = () => {
        const currentUrl = window.location.href;
        // Remove protocol for deep link
        const dappUrl = currentUrl.replace(/^https?:\/\//, '');
        const metamaskDeepLink = `https://metamask.app.link/dapp/${dappUrl}`;
        
        console.log('📱 Opening MetaMask via deep link:', metamaskDeepLink);
        window.location.href = metamaskDeepLink;
    };

    const connectWallet = async () => {
        setError('');
        setSuccessMessage('');
        setIsConnecting(true);

        try {
            console.log('🔗 Starting wallet connection...');
            console.log('📱 Is Mobile:', isMobile);
            console.log('🦊 Has Ethereum Provider:', hasEthereumProvider);
            console.log('🦊 Is MetaMask Browser:', isMetaMaskBrowser);

            // Mobile handling
            if (isMobile) {
                if (hasEthereumProvider && isMetaMaskBrowser) {
                    // User is inside MetaMask browser - connect directly
                    console.log('📱 Inside MetaMask browser - connecting directly');
                } else {
                    // User is in Safari/Chrome - redirect to MetaMask app
                    console.log('📱 Not in MetaMask browser - opening deep link');
                    setIsConnecting(false);
                    openMetaMaskDeepLink();
                    return; // Connection will complete after redirect
                }
            } else {
                // Desktop - check for MetaMask
                if (!hasEthereumProvider) {
                    throw new Error('MetaMask is not installed. Please install MetaMask to continue.');
                }
            }

            const accounts = await window.ethereum.request({ 
                method: 'eth_requestAccounts' 
            });
            
            if (accounts.length === 0) {
                throw new Error('No accounts found. Please unlock MetaMask.');
            }

            const address = accounts[0].toLowerCase();
            console.log('📍 Wallet address:', address);
            setCurrentAddress(address);

            // Step 1: Get signing message
            console.log('📝 Requesting signing message...');
            const messageResponse = await fetch(`${process.env.REACT_APP_API_URL}/blockchain/wallet/message`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${userToken}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ 
                    walletAddress: address 
                })
            });

            console.log('📡 Message response status:', messageResponse.status);

            if (!messageResponse.ok) {
                const errorData = await messageResponse.json();
                console.error('❌ Message error:', errorData);
                throw new Error(errorData.error || 'Failed to generate signing message');
            }

            const { message, timestamp } = await messageResponse.json();
            console.log('✅ Message received:', message);
            console.log('⏰ Timestamp:', timestamp);

            // Step 2: Request signature
            console.log('✍️ Requesting signature...');
            const provider = new ethers.BrowserProvider(window.ethereum);
            const signer = await provider.getSigner();
            const signature = await signer.signMessage(message);
            console.log('✅ Signature obtained:', signature.substring(0, 20) + '...');

            // Step 3: Verify and link wallet
            console.log('🔐 Verifying signature...');
            const linkPayload = {
                walletAddress: address,
                signature: signature,
                message: message,
                timestamp: timestamp
            };
            
            console.log('📤 Sending link request');

            const linkResponse = await fetch(`${process.env.REACT_APP_API_URL}/blockchain/wallet/link`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${userToken}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(linkPayload)
            });

            console.log('📡 Link response status:', linkResponse.status);

            if (!linkResponse.ok) {
                const errorData = await linkResponse.json();
                console.error('❌ Link error:', errorData);
                throw new Error(errorData.error || 'Failed to link wallet');
            }

            const result = await linkResponse.json();
            console.log('✅ Link successful:', result);
            
            setSuccessMessage('Wallet linked successfully! 🎉');
            await checkWalletStatus();

        } catch (err) {
            console.error('❌ Wallet connection error:', err);
            setError(err.message || 'Failed to connect wallet');
        } finally {
            setIsConnecting(false);
        }
    };

    const unlinkWallet = async () => {
        if (!window.confirm('Are you sure you want to unlink your wallet? This action requires that you have no pending claims.')) {
            return;
        }

        setError('');
        setSuccessMessage('');
        setIsConnecting(true);

        try {
            console.log('🔓 Unlinking wallet...');
            const response = await fetch(`${process.env.REACT_APP_API_URL}/blockchain/wallet/unlink`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${userToken}`,
                    'Content-Type': 'application/json'
                }
            });

            console.log('📡 Unlink response status:', response.status);

            if (!response.ok) {
                const errorData = await response.json();
                console.error('❌ Unlink error:', errorData);
                throw new Error(errorData.error || 'Failed to unlink wallet');
            }

            console.log('✅ Wallet unlinked successfully');
            setSuccessMessage('Wallet unlinked successfully');
            setCurrentAddress(null);
            await checkWalletStatus();

        } catch (err) {
            console.error('❌ Unlink error:', err);
            setError(err.message || 'Failed to unlink wallet');
        } finally {
            setIsConnecting(false);
        }
    };

    const formatAddress = (address) => {
        if (!address) return '';
        return `${address.substring(0, 6)}...${address.substring(address.length - 4)}`;
    };

    if (isLoading) {
        return (
            <div className="wallet-connect-card">
                <div className="wallet-loading">
                    <Loader className="spinner" size={24} />
                    <p>Loading wallet status...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="wallet-connect-card">
            <div className="wallet-header">
                <div className="wallet-title">
                    <Wallet size={24} />
                    <h3>Blockchain Wallet</h3>
                </div>
                {walletStatus.isLinked && (
                    <div className="wallet-badge linked">
                        <CheckCircle size={16} />
                        Linked
                    </div>
                )}
            </div>

            {error && (
                <div className="wallet-alert error">
                    <AlertCircle size={18} />
                    <span>{error}</span>
                </div>
            )}

            {successMessage && (
                <div className="wallet-alert success">
                    <CheckCircle size={18} />
                    <span>{successMessage}</span>
                </div>
            )}

            <div className="wallet-content">
                {!walletStatus.isLinked ? (
                    <>
                        <div className="wallet-info-box">
                            <Info size={20} />
                            <div className="info-text">
                                <h4>Why link your wallet?</h4>
                                <ul>
                                    <li>Claim your TPHILE tokens when TGE launches</li>
                                    <li>Required for instant and vested rewards</li>
                                    <li>Secure ownership verification via signature</li>
                                </ul>
                            </div>
                        </div>

                        {currentAddress && (
                            <div className="current-wallet-info">
                                <Shield size={18} />
                                <div>
                                    <span className="wallet-label">Connected Wallet</span>
                                    <span className="wallet-address">{formatAddress(currentAddress)}</span>
                                </div>
                            </div>
                        )}

                        <button 
                            className="wallet-connect-btn primary"
                            onClick={connectWallet}
                            disabled={isConnecting}
                        >
                            {isConnecting ? (
                                <>
                                    <Loader className="spinner" size={18} />
                                    Connecting...
                                </>
                            ) : isMobile && !hasEthereumProvider ? (
                                <>
                                    <ExternalLink size={18} />
                                    Open in MetaMask App
                                </>
                            ) : (
                                <>
                                    <LinkIcon size={18} />
                                    {currentAddress ? 'Link This Wallet' : 'Connect Wallet'}
                                </>
                            )}
                        </button>

                        <div className="wallet-footer-note">
                            {isMobile ? (
                                <p>
                                    Don't have MetaMask?{' '}
                                    <a 
                                        href="https://metamask.io/download/" 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="metamask-link"
                                    >
                                        Download the App <ExternalLink size={12} />
                                    </a>
                                </p>
                            ) : (
                                <p>
                                    Don't have a wallet? Install{' '}
                                    <a 
                                        href="https://metamask.io/download/" 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="metamask-link"
                                    >
                                        MetaMask <ExternalLink size={12} />
                                    </a>
                                </p>
                            )}
                        </div>
                    </>
                ) : (
                    <>
                        <div className="linked-wallet-info">
                            <div className="linked-wallet-row">
                                <span className="wallet-label">Wallet Address</span>
                                <div className="wallet-address-display">
                                    <span className="wallet-address">
                                        {formatAddress(walletStatus.walletAddress)}
                                    </span>
                                    <a
                                        href={`https://polygonscan.com/address/${walletStatus.walletAddress}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="explorer-link"
                                        title="View on PolygonScan (Polygon Mainnet)"
                                    >
                                        <ExternalLink size={14} />
                                    </a>
                                </div>
                            </div>

                            {walletStatus.linkedAt && (
                                <div className="linked-wallet-row">
                                    <span className="wallet-label">Linked On</span>
                                    <span className="wallet-value">
                                        {new Date(walletStatus.linkedAt).toLocaleDateString('en-US', {
                                            year: 'numeric',
                                            month: 'long',
                                            day: 'numeric'
                                        })}
                                    </span>
                                </div>
                            )}

                            <div className="linked-wallet-row">
                                <span className="wallet-label">Signature Verified</span>
                                <span className="wallet-value">
                                    {walletStatus.signatureVerified ? (
                                        <span className="verified">
                                            <CheckCircle size={16} /> Verified
                                        </span>
                                    ) : (
                                        <span className="unverified">
                                            <AlertCircle size={16} /> Not Verified
                                        </span>
                                    )}
                                </span>
                            </div>
                        </div>

                        <div className="wallet-actions">
                            <button 
                                className="wallet-connect-btn secondary"
                                onClick={unlinkWallet}
                                disabled={isConnecting}
                            >
                                {isConnecting ? (
                                    <>
                                        <Loader className="spinner" size={18} />
                                        Unlinking...
                                    </>
                                ) : (
                                    <>
                                        <Unlink size={18} />
                                        Unlink Wallet
                                    </>
                                )}
                            </button>
                        </div>

                        <div className="wallet-success-note">
                            <CheckCircle size={16} />
                            <span>You're all set! You'll be able to claim tokens once TGE is activated.</span>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default WalletConnect;
